import { Link } from 'react-router-dom';
import {
  Code,
  ExternalLink,
  Palette,
  Layout,
  Sparkles,
  ArrowRight,
  Copy,
  Check,
  Monitor,
  Smartphone,
  Globe,
  type LucideIcon,
} from 'lucide-react';
import { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { WIDGET_VERSION } from '@/lib/constants';

/**
 * Reset CSS variables to default gold branding
 * This ensures DevHome always has consistent styling
 * even after navigating back from a themed property page
 */
function useResetToDefaultTheme() {
  useEffect(() => {
    const root = document.documentElement;

    // Reset to default gold primary color from globals.css
    root.style.setProperty('--primary', '43 74% 38%');
    root.style.setProperty('--primary-foreground', '0 0% 100%');
    root.style.setProperty('--ring', '43 74% 38%');
    root.style.setProperty('--accent', '43 74% 95%');
    root.style.setProperty('--accent-foreground', '43 74% 30%');

    // Cleanup on unmount - remove inline styles to let CSS take over
    return () => {
      root.style.removeProperty('--primary');
      root.style.removeProperty('--primary-foreground');
      root.style.removeProperty('--ring');
      root.style.removeProperty('--accent');
      root.style.removeProperty('--accent-foreground');
    };
  }, []);
}

/**
 * DevHome - Developer homepage for the booking widget
 *
 * Shows:
 * - How to embed the widget
 * - Demo property links
 * - Configuration options
 * - Landing page upgrade info
 */
export function DevHome() {
  // Reset theme to gold branding on mount
  useResetToDefaultTheme();

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border/60 bg-card">
        <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl bg-primary/10 flex items-center justify-center">
                <span className="font-serif text-lg font-semibold text-primary">
                  E
                </span>
              </div>
              <div>
                <h1 className="font-serif text-xl font-semibold text-foreground">
                  ExperienceLocal Widget
                </h1>
                <p className="text-xs text-muted-foreground">
                  v{WIDGET_VERSION} · Developer Preview
                </p>
              </div>
            </div>
            <a
              href="https://experiencelocal.io"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
            >
              Main Site
              <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-4 py-12 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <section className="text-center mb-16">
          <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-foreground mb-4">
            Embeddable Booking Widget
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            Beautiful, customizable booking experiences for hotels and
            hospitality properties. Embed on any website or use as a standalone
            landing page.
          </p>
        </section>

        {/* Demo Properties */}
        <section className="mb-16">
          <h3 className="text-xs font-medium uppercase tracking-elegant text-muted-foreground mb-6">
            Demo Properties
          </h3>
          <div className="grid gap-6 md:grid-cols-2">
            <DemoPropertyCard
              name="The Oceanview Resort & Spa"
              slug="demo-oceanview"
              description="Luxury beachfront resort with water sports, wellness, and culinary experiences."
              experienceCount={4}
              location="Santa Monica, CA"
            />
            <DemoPropertyCard
              name="Mountain Lodge Retreat"
              slug="demo-mountain"
              description="Cozy mountain lodge with outdoor adventures, hiking, and stargazing."
              experienceCount={4}
              location="Lake Tahoe, CA"
            />
          </div>
        </section>

        {/* Integration Options */}
        <section className="mb-16">
          <h3 className="text-xs font-medium uppercase tracking-elegant text-muted-foreground mb-6">
            Integration Options
          </h3>
          <div className="grid gap-6 md:grid-cols-2">
            {/* Embed Mode */}
            <div className="rounded-xl border border-border/60 bg-card p-6 shadow-soft">
              <div className="flex items-start gap-4 mb-4">
                <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Code className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h4 className="font-semibold text-foreground mb-1">
                    Embed Widget
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    Add the booking widget to any website with a simple script
                    tag. Perfect for hotel websites.
                  </p>
                </div>
              </div>
              <CodeBlock
                code={`<div id="el-widget"></div>
<script
  src="https://widget.experiencelocal.io/embed.js"
  data-property="demo-oceanview"
  data-primary-color="#b8860b"
></script>`}
              />
              <div className="mt-4 flex items-center gap-2">
                <Link
                  to="/embed/demo-oceanview"
                  className="text-sm text-primary hover:underline flex items-center gap-1"
                >
                  View Embed Demo
                  <ArrowRight className="h-3 w-3" />
                </Link>
              </div>
            </div>

            {/* Landing Mode */}
            <div className="rounded-xl border border-border/60 bg-card p-6 shadow-soft">
              <div className="flex items-start gap-4 mb-4">
                <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                  <Globe className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h4 className="font-semibold text-foreground mb-1">
                    Standalone Landing Page
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    Full-page booking site with header, footer, and SEO. Get a
                    custom subdomain.
                  </p>
                </div>
              </div>
              <div className="bg-muted/50 rounded-lg p-4 font-mono text-sm">
                <p className="text-muted-foreground mb-2">Your custom URL:</p>
                <p className="text-foreground">
                  https://<span className="text-primary">your-hotel</span>
                  .experiencelocal.io
                </p>
              </div>
              <div className="mt-4">
                <span className="inline-flex items-center gap-1.5 text-xs font-medium text-primary bg-primary/10 px-2 py-1 rounded-full">
                  <Sparkles className="h-3 w-3" />
                  Upgrade Required
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Customization */}
        <section className="mb-16">
          <h3 className="text-xs font-medium uppercase tracking-elegant text-muted-foreground mb-6">
            Customization Options
          </h3>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <FeatureCard
              icon={Palette}
              title="Brand Colors"
              description="Match your hotel's brand with custom primary colors"
            />
            <FeatureCard
              icon={Layout}
              title="Layout Options"
              description="Grid, list, or calendar view for experiences"
            />
            <FeatureCard
              icon={Monitor}
              title="Responsive"
              description="Looks great on desktop, tablet, and mobile"
            />
            <FeatureCard
              icon={Smartphone}
              title="Dark Mode"
              description="Automatic light/dark theme support"
            />
          </div>
        </section>

        {/* Quick Links */}
        <section className="rounded-xl border border-border/60 bg-muted/30 p-6">
          <h3 className="text-xs font-medium uppercase tracking-elegant text-muted-foreground mb-4">
            Quick Links
          </h3>
          <div className="flex flex-wrap gap-4">
            <QuickLink href="/embed/demo-oceanview" label="Oceanview Embed" />
            <QuickLink href="/embed/demo-mountain" label="Mountain Lodge Embed" />
            <QuickLink
              href="/embed/demo-oceanview/sunrise-beach-yoga"
              label="Experience Detail"
            />
            <QuickLink
              href="/embed/demo-oceanview/sunrise-beach-yoga/book"
              label="Booking Flow"
            />
            <QuickLink href="/success" label="Success Page" />
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-border/60 mt-16">
        <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-sm text-muted-foreground">
              ExperienceLocal Booking Widget · Development Mode
            </p>
            <p className="text-sm text-muted-foreground">
              Seed data from{' '}
              <code className="bg-muted px-1.5 py-0.5 rounded text-xs">
                npm run db:seed
              </code>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

function DemoPropertyCard({
  name,
  slug,
  description,
  experienceCount,
  location,
  isDraft = false,
}: {
  name: string;
  slug: string;
  description: string;
  experienceCount: number;
  location: string;
  isDraft?: boolean;
}) {
  return (
    <div
      className={cn(
        'rounded-xl border bg-card p-6 shadow-soft',
        'transition-all duration-300 hover:shadow-elevated hover:-translate-y-1',
        isDraft ? 'border-border/40 opacity-75' : 'border-border/60'
      )}
    >
      <div className="flex items-start justify-between mb-3">
        <h4 className="font-semibold text-foreground">{name}</h4>
        {isDraft && (
          <span className="text-xs bg-muted px-2 py-0.5 rounded-full text-muted-foreground">
            Draft
          </span>
        )}
      </div>
      <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
        {description}
      </p>
      <div className="flex items-center gap-4 text-xs text-muted-foreground mb-4">
        <span>{experienceCount} experiences</span>
        <span>·</span>
        <span>{location}</span>
      </div>
      <div className="flex gap-3">
        <Link
          to={`/embed/${slug}`}
          className={cn(
            'flex-1 text-center text-sm font-medium py-2 px-4 rounded-lg transition-colors',
            'bg-primary text-primary-foreground hover:bg-primary/90'
          )}
        >
          Embed View
        </Link>
        {/* Use anchor tag for full page reload to reinitialize PropertyContext */}
        <a
          href={`/?property=${slug}`}
          className={cn(
            'flex-1 text-center text-sm font-medium py-2 px-4 rounded-lg transition-colors',
            'border border-border hover:bg-muted'
          )}
        >
          Landing View
        </a>
      </div>
    </div>
  );
}

function FeatureCard({
  icon: Icon,
  title,
  description,
}: {
  icon: LucideIcon;
  title: string;
  description: string;
}) {
  return (
    <div className="rounded-lg border border-border/60 bg-card p-4">
      <Icon className="h-5 w-5 text-primary mb-3" strokeWidth={1.5} />
      <h4 className="font-medium text-foreground text-sm mb-1">{title}</h4>
      <p className="text-xs text-muted-foreground">{description}</p>
    </div>
  );
}

function CodeBlock({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="relative">
      <pre className="bg-muted/50 rounded-lg p-4 overflow-x-auto text-xs font-mono text-foreground">
        <code>{code}</code>
      </pre>
      <button
        onClick={handleCopy}
        className={cn(
          'absolute top-2 right-2 p-1.5 rounded-md transition-colors',
          'hover:bg-muted text-muted-foreground hover:text-foreground'
        )}
        aria-label="Copy code"
      >
        {copied ? (
          <Check className="h-4 w-4 text-green-600" />
        ) : (
          <Copy className="h-4 w-4" />
        )}
      </button>
    </div>
  );
}

function QuickLink({ href, label }: { href: string; label: string }) {
  return (
    <Link
      to={href}
      className={cn(
        'text-sm px-3 py-1.5 rounded-lg transition-colors',
        'bg-card border border-border/60 text-foreground',
        'hover:border-primary/50 hover:text-primary'
      )}
    >
      {label}
    </Link>
  );
}
